import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { AtivacaoMAC, Evento, LocalAtivacao, PlanejamentoEventoMAC } from '@/lib/types';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { Skeleton } from '@/components/Skeleton';
import {
  AlertCircle, CalendarCheck, CheckCircle2, ChevronDown, ChevronRight,
  Link2, Loader2, MapPin, Save, Search, ShieldCheck, Unlink, Wifi, X,
} from 'lucide-react';
import clsx from 'clsx';

/**
 * Planejamento de Ativações — S3 v2 (07/07/2026)
 * O time MAC VINCULA explicitamente os eventos em que vai atuar (planeja
 * 2-3 meses à frente) — a sidebar mostra só os vinculados, sem a poluição
 * do calendário inteiro. Canais são definidos POR ativação; "Confirmar
 * planejamento" congela e gera as verificações por time no worker.
 */

/** UF do evento: prefixo "[XX]" no nome, ou campo Polo (UF). */
function ufDoEvento(ev: Evento): string | null {
  const m = ev.nome?.match(/^\[([A-Z]{2,3})\]/);
  return m?.[1] ?? (ev as { uf?: string }).uf ?? null;
}

export default function MacPlanejamento() {
  const { user } = useAuth();
  const [selectedEventoId, setSelectedEventoId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Filtros da sidebar (eventos já planejados)
  const [buscaSide, setBuscaSide] = useState('');
  const [ufSide, setUfSide] = useState('todos');
  const [deSide, setDeSide] = useState('');
  const [ateSide, setAteSide] = useState('');

  const { data: eventos, isLoading: loadingEventos } = useQuery({
    queryKey: ['eventos'],
    queryFn: () => api.eventos.list().then((r) => r.data),
  });
  const { data: ativacoes } = useQuery({
    queryKey: ['mac-ativacoes'],
    queryFn: () => api.mac.ativacoes.list().then((r) => r.data),
  });
  const { data: locais } = useQuery({
    queryKey: ['mac-locais'],
    queryFn: () => api.mac.locais.list().then((r) => r.data),
  });
  const { data: planejamentos } = useQuery({
    queryKey: ['mac-planejamento'],
    queryFn: () => api.mac.planejamento.list().then((r) => r.data),
  });
  const planQ = useQuery({
    queryKey: ['mac-planejamento', selectedEventoId],
    queryFn: () => (selectedEventoId ? api.mac.planejamento.get(selectedEventoId).then((r) => r.data) : null),
    enabled: !!selectedEventoId,
  });

  const canEdit = user?.papel === 'Coordenação' || user?.papel === 'Diretoria';

  // Sidebar: SÓ eventos vinculados (com planejamento), ordenados por data
  const vinculados = useMemo(() => {
    if (!eventos || !planejamentos) return [];
    const comPlano = new Map(planejamentos.map((p) => [p.eventoId, p]));
    return eventos
      .filter((e) => comPlano.has(e.id))
      .sort((a, b) => (a.dataInicio ?? '').localeCompare(b.dataInicio ?? ''))
      .map((e) => ({ evento: e, plano: comPlano.get(e.id)! }));
  }, [eventos, planejamentos]);

  const ufsVinculados = useMemo(() => {
    const s = new Set<string>();
    for (const v of vinculados) { const u = ufDoEvento(v.evento); if (u) s.add(u); }
    return [...s].sort();
  }, [vinculados]);

  const filtroSideAtivo = !!buscaSide.trim() || ufSide !== 'todos' || !!deSide || !!ateSide;
  const vinculadosFiltrados = useMemo(() => vinculados
    .filter(({ evento: e }) => !buscaSide.trim() || e.nome.toLowerCase().includes(buscaSide.trim().toLowerCase()))
    .filter(({ evento: e }) => ufSide === 'todos' || ufDoEvento(e) === ufSide)
    .filter(({ evento: e }) => {
      if (!deSide && !ateSide) return true;
      const d = e.dataInicio ? e.dataInicio.slice(0, 10) : '';
      if (!d) return false;
      if (deSide && d < deSide) return false;
      if (ateSide && d > ateSide) return false;
      return true;
    }),
  [vinculados, buscaSide, ufSide, deSide, ateSide]);

  const selectedEvento = vinculados.find((v) => v.evento.id === selectedEventoId)?.evento ?? null;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Planejamento de Ativações"
        subtitle="Vincule os eventos em que a MAC vai atuar e defina os canais de cada ativação"
      />

      <div className="flex gap-4 min-h-[500px]">
        {/* Sidebar — só eventos vinculados */}
        <div className="w-64 shrink-0 bg-panel border border-border rounded-lg overflow-y-auto max-h-[calc(100vh-200px)]">
          <div className="p-3 border-b border-border space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-muted uppercase tracking-wide">Eventos da MAC</h3>
              <span className="text-[10px] text-muted">
                {filtroSideAtivo ? `${vinculadosFiltrados.length}/${vinculados.length}` : vinculados.length}
              </span>
            </div>
            {canEdit && (
              <button type="button" onClick={() => setPickerOpen(true)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-blue text-white text-xs font-medium hover:bg-blue/90 transition-colors">
                <Link2 className="w-3.5 h-3.5" /> Vincular evento
              </button>
            )}
            {vinculados.length > 0 && (
              <div className="space-y-1.5 pt-0.5">
                <div className="relative">
                  <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
                  <input
                    className="input w-full !pl-7 !py-1 text-[11px]"
                    placeholder="Filtrar evento…"
                    value={buscaSide}
                    onChange={(e) => setBuscaSide(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <select className="input !py-1 !px-1.5 text-[10px]" value={ufSide}
                    onChange={(e) => setUfSide(e.target.value)} title="Estado">
                    <option value="todos">Estado: todos</option>
                    {ufsVinculados.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  {filtroSideAtivo ? (
                    <button type="button"
                      onClick={() => { setBuscaSide(''); setUfSide('todos'); setDeSide(''); setAteSide(''); }}
                      className="text-[10px] text-blue-light hover:underline text-left px-1">
                      limpar filtros
                    </button>
                  ) : <span />}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <label className="flex flex-col gap-0.5">
                    <span className="text-[8px] uppercase tracking-wider text-muted">De</span>
                    <input type="date" className="input !py-1 !px-1.5 text-[10px]" value={deSide}
                      onChange={(e) => setDeSide(e.target.value)} />
                  </label>
                  <label className="flex flex-col gap-0.5">
                    <span className="text-[8px] uppercase tracking-wider text-muted">Até</span>
                    <input type="date" className="input !py-1 !px-1.5 text-[10px]" value={ateSide}
                      onChange={(e) => setAteSide(e.target.value)} />
                  </label>
                </div>
              </div>
            )}
          </div>
          {loadingEventos ? (
            <div className="p-3 space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : vinculados.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-muted leading-relaxed">
              Nenhum evento vinculado ainda.<br />
              {canEdit ? 'Use "Vincular evento" para escolher onde a MAC vai atuar.' : 'A Coordenação vincula os eventos da MAC.'}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {vinculadosFiltrados.length === 0 && (
                <div className="px-4 py-6 text-center text-[11px] text-muted">Nenhum evento com esses filtros.</div>
              )}
              {vinculadosFiltrados.map(({ evento: ev, plano }) => (
                <button
                  key={ev.id}
                  type="button"
                  onClick={() => setSelectedEventoId(ev.id)}
                  className={clsx(
                    'w-full text-left px-3 py-2.5 flex items-center gap-2 transition-colors',
                    selectedEventoId === ev.id ? 'bg-blue/10 text-blue' : 'hover:bg-panel-2',
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{ev.nome}</div>
                    <div className="text-[10px] text-muted">
                      {ev.dataInicio ? new Date(ev.dataInicio).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : '—'}
                      {plano.status === 'confirmado' ? ' · confirmado' : ' · rascunho'}
                    </div>
                  </div>
                  {plano.status === 'confirmado' && <ShieldCheck className="w-3 h-3 text-green shrink-0" />}
                  <ChevronRight className="w-3 h-3 text-muted shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Painel principal */}
        <div className="flex-1 bg-panel border border-border rounded-lg p-5">
          {!selectedEvento ? (
            <div className="flex items-center justify-center h-full text-muted text-sm">
              <CalendarCheck className="w-5 h-5 mr-2 opacity-50" />
              {vinculados.length === 0 ? 'Vincule um evento para começar' : 'Selecione um evento na sidebar'}
            </div>
          ) : planQ.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted"><Loader2 className="w-4 h-4 animate-spin" /> Carregando planejamento…</div>
          ) : (
            <PainelPlanejamento
              key={`${selectedEvento.id}-${planQ.data?.id ?? 'new'}`}
              evento={selectedEvento}
              plan={planQ.data ?? null}
              ativacoes={ativacoes ?? []}
              locais={locais ?? []}
              canEdit={canEdit}
              onDesvincular={() => setSelectedEventoId(null)}
            />
          )}
        </div>
      </div>

      {pickerOpen && (
        <VincularEventoModal
          eventos={eventos ?? []}
          jaVinculados={new Set(vinculados.map((v) => v.evento.id))}
          onClose={() => setPickerOpen(false)}
          onVinculado={(id) => { setSelectedEventoId(id); setPickerOpen(false); }}
        />
      )}
    </div>
  );
}

/* ─── Modal: vincular evento (busca + UF + período) ───────────────────────── */
function VincularEventoModal({
  eventos, jaVinculados, onClose, onVinculado,
}: {
  eventos: Evento[];
  jaVinculados: Set<string>;
  onClose: () => void;
  onVinculado: (eventoId: string) => void;
}) {
  const qc = useQueryClient();
  const [busca, setBusca] = useState('');
  const [uf, setUf] = useState('todos');
  const [meses, setMeses] = useState<3 | 6 | 0>(3); // 0 = todos futuros
  const [err, setErr] = useState<string | null>(null);

  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const limite = meses === 0 ? null : new Date(hoje.getFullYear(), hoje.getMonth() + meses, hoje.getDate());

  const candidatos = useMemo(() => eventos
    .filter((e) => e.tipoEvento !== 'interno' && e.tipoEvento !== 'feriado')
    .filter((e) => !jaVinculados.has(e.id))
    .filter((e) => {
      const d = e.dataInicio ? new Date(e.dataInicio) : null;
      if (!d) return false;
      if (d < hoje) return false;
      if (limite && d > limite) return false;
      return true;
    })
    .filter((e) => uf === 'todos' || ufDoEvento(e) === uf)
    .filter((e) => !busca.trim() || e.nome.toLowerCase().includes(busca.trim().toLowerCase()))
    .sort((a, b) => (a.dataInicio ?? '').localeCompare(b.dataInicio ?? '')),
  [eventos, jaVinculados, busca, uf, meses]);

  const ufs = useMemo(() => {
    const s = new Set<string>();
    for (const e of eventos) { const u = ufDoEvento(e); if (u) s.add(u); }
    return [...s].sort();
  }, [eventos]);

  const vincularMut = useMutation({
    mutationFn: (eventoId: string) => api.mac.planejamento.put(eventoId, { itens: [] }),
    onSuccess: (_res, eventoId) => {
      qc.invalidateQueries({ queryKey: ['mac-planejamento'] });
      onVinculado(eventoId);
    },
    onError: (e) => setErr(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? e.message) : String(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6" tabIndex={-1} ref={(el)=>{if(el&&el.dataset.esc!=='1'){el.dataset.esc='1';el.focus();}}} onKeyDown={(e)=>{if(e.key==='Escape')onClose();}}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-panel border border-border rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div>
            <h2 className="text-sm font-bold">Vincular evento à MAC</h2>
            <p className="text-[10px] text-muted mt-0.5">Escolha em quais eventos o time vai atuar — só eles aparecem no planejamento</p>
          </div>
          <button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
        </div>

        {/* Filtros: busca · UF · período */}
        <div className="px-5 py-3 border-b border-border space-y-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
            <input
              autoFocus
              className="input w-full !pl-8 text-xs"
              placeholder="Buscar evento pelo nome…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select className="input !py-1 !px-2 text-[11px]" value={uf} onChange={(e) => setUf(e.target.value)} title="Estado">
              <option value="todos">Estado: todos</option>
              {ufs.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            <div className="flex gap-1">
              {([[3, '3 meses'], [6, '6 meses'], [0, 'Todos futuros']] as const).map(([v, label]) => (
                <button key={v} type="button" onClick={() => setMeses(v)}
                  className={clsx(
                    'px-2 py-1 text-[10px] font-semibold rounded border transition-colors',
                    meses === v ? 'bg-blue/15 text-blue-light border-blue/40' : 'bg-panel text-text-2 border-border hover:border-border-light',
                  )}>
                  {label}
                </button>
              ))}
            </div>
            <span className="text-[10px] text-muted ml-auto">{candidatos.length} evento{candidatos.length !== 1 ? 's' : ''}</span>
          </div>
          {err && (
            <div className="text-xs text-red bg-red/10 border border-red/30 rounded px-2 py-1.5 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{err}</span>
            </div>
          )}
        </div>

        {/* Lista */}
        <div className="flex-1 overflow-y-auto divide-y divide-border">
          {candidatos.map((ev) => (
            <button
              key={ev.id}
              type="button"
              disabled={vincularMut.isPending}
              onClick={() => vincularMut.mutate(ev.id)}
              className="w-full text-left px-5 py-2.5 flex items-center gap-3 hover:bg-panel-2 disabled:opacity-50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{ev.nome}</div>
                <div className="text-[10px] text-muted">
                  {ev.dataInicio ? new Date(ev.dataInicio).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' }) : 'sem data'}
                  {ev.local && ` · ${ev.local}`}
                </div>
              </div>
              {vincularMut.isPending && vincularMut.variables === ev.id
                ? <Loader2 className="w-3.5 h-3.5 animate-spin text-blue shrink-0" />
                : <Link2 className="w-3.5 h-3.5 text-muted shrink-0" />}
            </button>
          ))}
          {candidatos.length === 0 && (
            <div className="px-5 py-8 text-center text-xs text-muted">Nenhum evento encontrado com esses filtros.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Painel do planejamento (ativações × canais) ─────────────────────────── */
function PainelPlanejamento({
  evento, plan, ativacoes, locais, canEdit, onDesvincular,
}: {
  evento: Evento;
  plan: PlanejamentoEventoMAC | null;
  ativacoes: AtivacaoMAC[];
  locais: LocalAtivacao[];
  canEdit: boolean;
  onDesvincular: () => void;
}) {
  const qc = useQueryClient();
  const [itens, setItens] = useState<Record<string, string[]>>(() => {
    const init: Record<string, string[]> = {};
    for (const i of plan?.itens ?? []) init[i.ativacaoId] = i.canalIds;
    if (!plan?.itens?.length && plan?.ativacaoIds?.length) {
      for (const a of plan.ativacaoIds) init[a] = plan.localIds ?? [];
    }
    return init;
  });
  const [notas, setNotas] = useState(plan?.notasEstrategicas ?? '');
  const [aberta, setAberta] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [confirmResult, setConfirmResult] = useState<{ cardsGerados: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => { setConfirmResult(null); setErr(null); setEditMode(false); }, [evento.id]);

  const confirmado = plan?.status === 'confirmado';
  const selecionadas = Object.keys(itens);
  const showSummary = confirmado && !editMode;
  const porCategoria = useMemo(() => {
    const g = new Map<string, AtivacaoMAC[]>();
    for (const a of ativacoes) {
      const k = a.categoria ?? 'Outras';
      g.set(k, [...(g.get(k) ?? []), a]);
    }
    return [...g.entries()];
  }, [ativacoes]);

  const saveMut = useMutation({
    mutationFn: () => api.mac.planejamento.put(evento.id, {
      itens: Object.entries(itens).map(([ativacaoId, canalIds]) => ({ ativacaoId, canalIds })),
      notasEstrategicas: notas || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mac-planejamento'] });
      setDirty(false); setErr(null);
    },
    onError: (e) => setErr(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? e.message) : String(e)),
  });

  const confirmMut = useMutation({
    mutationFn: () => api.mac.planejamento.confirmar(evento.id),
    onSuccess: (res) => {
      setConfirmResult({ cardsGerados: res.cardsGerados });
      setErr(null);
      qc.invalidateQueries({ queryKey: ['mac-planejamento'] });
      qc.invalidateQueries({ queryKey: ['demandas'] });
    },
    onError: (e) => setErr(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? e.message) : String(e)),
  });

  const desvincularMut = useMutation({
    mutationFn: () => api.mac.planejamento.delete(evento.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mac-planejamento'] });
      onDesvincular();
    },
  });

  async function salvarEConfirmar() {
    const msg = `Confirmar o planejamento? ${dirty ? 'A seleção será salva e ' : ''}os cards de verificação por time serão gerados (D-15 físico / D-7 digital)${confirmado ? ' — re-confirmar só completa o que faltar' : ''}.`;
    if (!confirm(msg)) return;
    try {
      if (dirty) await saveMut.mutateAsync();
      confirmMut.mutate();
    } catch {
      /* erro do save já aparece via onError */
    }
  }

  function toggleAtivacao(id: string) {
    setItens((prev) => {
      const next = { ...prev };
      if (id in next) { delete next[id]; if (aberta === id) setAberta(null); }
      else { next[id] = []; setAberta(id); }
      return next;
    });
    setDirty(true);
  }
  function toggleCanal(ativacaoId: string, canalId: string) {
    setItens((prev) => {
      const cur = prev[ativacaoId] ?? [];
      return { ...prev, [ativacaoId]: cur.includes(canalId) ? cur.filter((c) => c !== canalId) : [...cur, canalId] };
    });
    setDirty(true);
  }

  const semCanal = selecionadas.filter((a) => (itens[a] ?? []).length === 0);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-base">{evento.nome}</h2>
          <p className="text-xs text-muted mt-0.5">
            {evento.dataInicio ? new Date(evento.dataInicio).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : 'Data indefinida'}
            {evento.local && ` · ${evento.local}`}
          </p>
        </div>
        {confirmado ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold bg-green/15 text-green border border-green/40">
            <ShieldCheck className="w-3 h-3" /> CONFIRMADO {plan?.dataConfirmacao ? `· ${new Date(`${plan.dataConfirmacao}T12:00:00`).toLocaleDateString('pt-BR')}` : ''}
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-bold bg-panel-2 text-text-2 border border-border">RASCUNHO</span>
        )}
      </div>

      {/* Ativações com canais por ativação */}
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-xs font-bold text-muted uppercase tracking-wide">Ativações × canais</h3>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-muted">{selecionadas.length} selecionada{selecionadas.length !== 1 ? 's' : ''}</span>
            {showSummary && (<button type="button" onClick={() => setEditMode(true)} className="text-[11px] font-semibold text-blue hover:underline">Editar planejamento</button>)}
            {confirmado && editMode && (<button type="button" onClick={() => setEditMode(false)} className="text-[11px] font-medium text-muted hover:text-text">Voltar ao resumo</button>)}
          </div>
        </div>
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
          {showSummary ? (
            selecionadas.length === 0 ? (
              <div className="text-xs text-muted">Nenhuma ativação selecionada.</div>
            ) : (
              Object.entries(itens).map(([aid, cids]) => {
                const a = ativacoes.find((x) => x.id === aid);
                const nomes = (cids ?? []).map((c) => locais.find((l) => l.id === c)?.nome).filter((n): n is string => Boolean(n));
                return (
                  <div key={aid} className="rounded-md border border-blue/40 bg-blue/5 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold truncate">{a?.nome ?? aid}</span>
                      <span className={clsx('text-[9px] font-bold px-1 py-0.5 rounded shrink-0', a?.status === 'ativa' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700')}>{a?.status === 'ativa' ? 'ATIVA' : 'A CRIAR'}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {nomes.length > 0 ? nomes.map((n) => (
                        <span key={n} className="text-[10px] px-1.5 py-0.5 rounded bg-panel-2 text-text-2">{n}</span>
                      )) : <span className="text-[10px] text-muted">sem canal</span>}
                    </div>
                  </div>
                );
              })
            )
          ) : (<>
          {porCategoria.map(([cat, lista]) => (
            <div key={cat}>
              <div className="text-[9px] font-bold uppercase tracking-wider text-muted mb-1">{cat}</div>
              <div className="space-y-1">
                {lista.map((a) => {
                  const sel = a.id in itens;
                  const canaisSel = itens[a.id] ?? [];
                  const open = aberta === a.id;
                  return (
                    <div key={a.id} className={clsx('rounded-md border transition-colors', sel ? 'border-blue/50 bg-blue/5' : 'border-border')}>
                      <div className="flex items-center gap-2 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={sel}
                          onChange={() => canEdit && toggleAtivacao(a.id)}
                          disabled={!canEdit}
                          className="rounded border-border"
                        />
                        <button type="button" className="flex-1 flex items-center gap-2 text-left min-w-0"
                          onClick={() => sel && setAberta(open ? null : a.id)}>
                          <span className="text-xs truncate">{a.nome}</span>
                          <span className={clsx(
                            'text-[9px] font-bold px-1 py-0.5 rounded shrink-0',
                            a.status === 'ativa' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700',
                          )}>
                            {a.status === 'ativa' ? 'ATIVA' : 'A CRIAR'}
                          </span>
                        </button>
                        {sel && (
                          <span className={clsx('text-[10px] shrink-0', canaisSel.length ? 'text-text-2' : 'text-yellow')}>
                            {canaisSel.length ? `${canaisSel.length} canal${canaisSel.length > 1 ? 'is' : ''}` : 'sem canal'}
                          </span>
                        )}
                        {sel && (open ? <ChevronDown className="w-3 h-3 text-muted" /> : <ChevronRight className="w-3 h-3 text-muted" />)}
                      </div>
                      {sel && open && (
                        <div className="px-3 pb-2.5 flex flex-wrap gap-1.5">
                          {locais.map((l) => {
                            const on = canaisSel.includes(l.id);
                            return (
                              <button key={l.id} type="button"
                                onClick={() => canEdit && toggleCanal(a.id, l.id)}
                                className={clsx(
                                  'flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[10px] font-medium transition-colors',
                                  on ? 'border-blue bg-blue/10 text-blue' : 'border-border text-muted hover:text-text',
                                  !canEdit && 'pointer-events-none',
                                )}>
                                {l.tipo === 'fisico' ? <MapPin className="w-2.5 h-2.5" /> : <Wifi className="w-2.5 h-2.5" />}
                                {l.nome}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          </>)}
        </div>
      </div>

      {/* Notas estratégicas */}
      <div>
        <h3 className="text-xs font-bold text-muted uppercase tracking-wide mb-2">Notas estratégicas</h3>
        <textarea
          value={notas}
          onChange={(e) => { setNotas(e.target.value); setDirty(true); }}
          rows={3}
          className="input-field text-xs"
          placeholder="Objetivos, foco de vendas, público-alvo..."
          disabled={!canEdit}
        />
      </div>

      {err && (
        <div className="text-xs text-red bg-red/10 border border-red/30 rounded px-2 py-1.5 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{err}</span>
        </div>
      )}
      {confirmResult && (
        <div className="bg-green/10 border border-green/30 rounded-lg p-3 text-green text-xs space-y-1">
          <p className="font-bold flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Planejamento confirmado — verificações geradas!</p>
          <p>{confirmResult.cardsGerados} card{confirmResult.cardsGerados !== 1 ? 's' : ''} de verificação/criação gerado{confirmResult.cardsGerados !== 1 ? 's' : ''} (prazos D-15 físico · D-7 digital). As ativações também entram como checks no card-mãe MAC do evento.</p>
        </div>
      )}

      {/* Ações */}
      {canEdit && (
        <div className="flex justify-between items-center gap-2">
          {!confirmado ? (
            <button
              type="button"
              disabled={desvincularMut.isPending}
              onClick={() => { if (confirm('Desvincular este evento da MAC? O rascunho de planejamento será removido.')) desvincularMut.mutate(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-red-200 text-red-500 text-xs font-medium hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              <Unlink className="w-3.5 h-3.5" />
              {desvincularMut.isPending ? 'Removendo...' : 'Desvincular evento'}
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            {semCanal.length > 0 && (
              <span className="text-[10px] text-yellow">{semCanal.length} ativação(ões) sem canal</span>
            )}
            <button
              type="button"
              disabled={!dirty || saveMut.isPending}
              onClick={() => saveMut.mutate()}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-blue text-blue text-xs font-medium hover:bg-blue/5 disabled:opacity-40 transition-colors"
            >
              <Save className="w-3.5 h-3.5" />
              {saveMut.isPending ? 'Salvando...' : 'Salvar rascunho'}
            </button>
            <button
              type="button"
              disabled={confirmMut.isPending || saveMut.isPending || selecionadas.length === 0}
              title={selecionadas.length === 0 ? 'Selecione ao menos uma ativação' : 'Salva a seleção e gera as verificações por time'}
              onClick={salvarEConfirmar}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-blue text-white text-xs font-medium hover:bg-blue/90 disabled:opacity-40 transition-colors"
            >
              {confirmMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
              {confirmado ? 'Re-confirmar planejamento' : 'Confirmar planejamento'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
